import { ContextMenuWrapper } from "components/ContextMenu/ContextMenu";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { imageProps } from "components/ImageIcon/ImageIcon";
import { ProvisioningStateDot } from "components/ProvisioningStateDot/ProvisioningStateDot";
import dotStyle from "components/ProvisioningStateDot/ProvisioningStateDot.module.scss";
import RecordLabel from "components/RecordLabel";
import { getRecordColumnCellStyle } from "components/Table/recordColumnWidths";
import { locationColumn } from "components/Table/recordTableUtils";
import { FeConfigType, FeRecordType } from "config/config";
import {
    executionActionsColumn,
    executionInfoColumn,
    executionNameColumn,
    executionPriorityColumn,
    executionStatusColumn,
    getScheduledForInfo,
} from "config/executions";
import { createdAtDto, createdByDto, modifiedAtDto, modifiedByDto } from "config/utils.jsx";
import {
    BootstrapUserBadge,
    BundleContentBadge,
    ChangedBundleBadge,
    DeprecationBadge,
    InTrashBadge,
    PendingDeleteBadge,
    PendingRestoreBadge,
    PendingTrashBadge,
    ReadOnlyBadges,
    SyntaxErrorBadge,
} from "containers/Resources/components/Badges/Badges";
import badgeStyle from "containers/Resources/components/Badges/Badges.module.scss";
import { StateBadges } from "containers/Resources/components/Badges/StateBadges";
import {
    RecordDescriptionInlineEditCell,
    RecordNameInlineEditPencil,
} from "containers/Resources/screens/ResourcesSingle/RecordFieldInlineEdit";
import { checkBootstrapUser } from "containers/Workspaces/utils";
import { Bundle } from "graphql/generated/graphql";
import { Dispatch } from "redux";
import { getRelativeUrl } from "utils/service";
import { MessageTypeData, RecordTypeData } from "../interfaces";

export const commonColumns = {
    name: ({
        dispatch,
        setNewValue,
        bootstrapUserName,
    }: {
        setNewValue?: (data) => void;
        dispatch?: Dispatch<any>;
        bootstrapUserName?: string;
    }) => ({
        label: "Name",
        key: "name",
        dto: (data: RecordTypeData, afterButtonAction?: () => void) => {
            const {
                resource_type,
                activity_type,
                id,
                name,
                description,
                resolved_icon,
                resolved_object_template_id,
                deleted_at,
                has_deprecation,
                has_syntax_error,
                is_bundle_content,
                bundle_id,
                pending_trash_identity_id,
                pending_restore_identity_id,
                pending_delete_identity_id,
                comment_count,
            } = data;
            const recordType = resource_type || activity_type;
            const isBootstrapUser = checkBootstrapUser({ name, bootstrapUserName, recordType });

            const recordLabel = (
                <RecordLabel
                    link={getRelativeUrl(recordType as FeConfigType, id)} // TODO (medium) fix typing
                    name={name}
                    description={description}
                    iconByType={recordType as FeRecordType} // TODO (medium) fix typing
                    image={resolved_icon}
                    commentCount={comment_count}
                    {...imageProps(
                        recordType as FeRecordType,
                        id,
                        // The template id is threaded into the icon resolver so a
                        // READY custom object resolves its template icon from the
                        // cached `objectTemplateSchema` store (the status-dot
                        // provisioning cache only holds non-READY COs). Prefer the
                        // nested reference if the row query selected it; fall back
                        // to the flat FK column (`object_template_id`), which most
                        // list/search queries carry even when they do not nest the
                        // `object_template_id_object_template { id }` sub-selection.
                        (data as {
                            object_template_id_object_template?: { id?: string };
                            object_template_id?: string;
                        })?.object_template_id_object_template?.id ??
                            (data as { object_template_id?: string })?.object_template_id ??
                            resolved_object_template_id
                    )}
                />
            );
            // The record name is the row's navigation link (RecordLabel). Editable
            // resource-class rows also get an inline name-edit pencil, rendered at
            // the TRAILING end of the name cell — after the state-badge strip — so
            // the pencil always sits at the right end of the row's icon set, never
            // between the name and the badges (where a reserved slot read as spurious
            // mid-row padding). The placement + hover-reveal is defined once on
            // `.nameCellWithBadges` (Badges.module.scss), shared across every
            // RecordTable name cell. RecordNameInlineEditPencil self-gates (resource
            // class + read-only/trash + container git-write-lock, reading gitLockData
            // from RouterContext) and renders nothing for a non-editable / activity
            // (execution) / locked row, so those rows keep just the navigation link
            // and reserve no pencil slot. `afterButtonAction` is the table's
            // row-refresh callback (re-read so a later edit sees fresh data).
            const content = (
                <div className={badgeStyle.nameCellWithBadges}>
                    <div className={dotStyle.relativePositionedContainer}>
                        {recordType === "custom_object" ? <ProvisioningStateDot id={id} /> : null}
                        {recordLabel}
                    </div>
                    <StateBadges>
                        <InTrashBadge deletedAt={deleted_at} />
                        <PendingTrashBadge pendingTrashIdentityId={pending_trash_identity_id} />
                        <PendingRestoreBadge
                            pendingRestoreIdentityId={pending_restore_identity_id}
                        />
                        <PendingDeleteBadge pendingDeleteIdentityId={pending_delete_identity_id} />
                        <ReadOnlyBadges data={data} />
                        <DeprecationBadge hasDeprecation={has_deprecation} />
                        <SyntaxErrorBadge hasSyntaxError={has_syntax_error} />
                        <BundleContentBadge isBundleContent={!!bundle_id && !!is_bundle_content} />
                        <ChangedBundleBadge
                            isDirty={(data as unknown as Bundle)?.is_dirty ?? undefined}
                        />
                        <BootstrapUserBadge isBootstrapUser={isBootstrapUser} />
                    </StateBadges>
                    <RecordNameInlineEditPencil
                        record={data}
                        resourceName={recordType}
                        refresh={afterButtonAction}
                    />
                </div>
            );
            return setNewValue ? (
                <ContextMenuWrapper
                    handleClick={() =>
                        setNewValue({
                            field: "name",
                            value: name,
                        })
                    }
                >
                    {content}
                </ContextMenuWrapper>
            ) : (
                content
            );
        },
        required: true,
        shown: true,
        sortable: true,
        cellStyle: getRecordColumnCellStyle("name"),
    }),
    // Addable (shown:false, so the default column set is unchanged) editable
    // Description column for the SHARED resource-list screens (advanced search +
    // resource listings) — the same pop-out markdown editor as the object_template
    // CO list and the dashboard record_list widget
    // (operator-task-fe-inline-edit-list-views-followups, item 1, second surface).
    // RecordDescriptionInlineEditCell is self-contained: it reads the container
    // git-write-lock state from RouterContext and gates editability per row
    // (isRecordFieldEditableWithGitLock), so a read-only / git-locked / non-resource
    // (execution/relationship) row shows only the clipped read-only preview.
    // `description` is already fetched for these lists (the name cell reads it).
    description: () => ({
        label: "Description",
        key: "description",
        required: false,
        shown: false,
        sortable: true,
        cellStyle: getRecordColumnCellStyle("description"),
        dto: (data: RecordTypeData, afterButtonAction?: () => void) => (
            <RecordDescriptionInlineEditCell
                record={data}
                resourceName={data.resource_type || data.activity_type}
                refresh={afterButtonAction}
            />
        ),
    }),
    id: (setNewValue?: (data) => void) => ({
        label: "Id",
        key: "id",
        shown: false,
        sortable: true,
        cellStyle: getRecordColumnCellStyle("id"),
        dto: (value) => {
            // One line, capped with a trailing ellipsis when the column is too
            // narrow to show the whole id — the same display/cap affordance as
            // the Name / Created-by columns. A bare id text node wraps to a
            // second line (default white-space) and is then clipped by the fixed
            // row height with no visible affordance.
            // (operator-task-fe-dedup-executions-column-config)
            const idText = (
                <span
                    style={{
                        display: "block",
                        maxWidth: "100%",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {value?.id}
                </span>
            );
            return setNewValue ? (
                <ContextMenuWrapper
                    handleClick={() =>
                        setNewValue({
                            field: "id",
                            value: value.id,
                        })
                    }
                >
                    {idText}
                </ContextMenuWrapper>
            ) : (
                idText
            );
        },
    }),
    createdAt: (setNewValue?: (data) => void) => ({
        label: "Created at",
        key: "created_at",
        required: false,
        shown: false,
        sortable: true,
        cellStyle: getRecordColumnCellStyle("created_at"),
        dto: (data: RecordTypeData) => createdAtDto(data, setNewValue),
    }),
    createdBy: (setNewValue?: (data) => void) => ({
        label: "Created by",
        key: "created_by",
        required: false,
        shown: false,
        cellStyle: getRecordColumnCellStyle("created_by"),
        dto: (data: RecordTypeData) => createdByDto(data, setNewValue),
    }),
    modifiedAt: (setNewValue?: (data) => void) => ({
        label: "Modified at",
        key: "modified_at",
        required: false,
        shown: true,
        sortable: true,
        cellStyle: getRecordColumnCellStyle("modified_at"),
        dto: (data: RecordTypeData) => modifiedAtDto(data, setNewValue),
    }),
    modifiedBy: (setNewValue?: (data) => void) => ({
        label: "Modified by",
        key: "modified_by",
        required: false,
        shown: true,
        cellStyle: getRecordColumnCellStyle("modified_by"),
        dto: (data: RecordTypeData) => modifiedByDto(data, setNewValue),
    }),
};

export const resourceColumns = (dispatch: Dispatch<any>, setNewValue: any, bootstrapUserName?: string) => [
    commonColumns.name({ dispatch, setNewValue, bootstrapUserName }),
    commonColumns.description(),
    locationColumn(setNewValue),
    commonColumns.id(setNewValue),
    commonColumns.modifiedAt(setNewValue),
    commonColumns.modifiedBy(setNewValue),
    commonColumns.createdAt(setNewValue),
    commonColumns.createdBy(setNewValue),
];

export const executionColumns = (paper, root, setNewValue) => [
    // Name + Status are now the SHARED execution column builders from
    // config/executions (consumed identically by the main Executions screen) so a
    // change to the executions list lands in one place
    // (operator-task-fe-dedup-executions-column-config).
    executionNameColumn(setNewValue),
    commonColumns.id(setNewValue),
    executionInfoColumn(setNewValue),
    executionStatusColumn(setNewValue, paper, root),
    commonColumns.modifiedAt(setNewValue),
    commonColumns.modifiedBy(setNewValue),
    commonColumns.createdAt(setNewValue),
    commonColumns.createdBy(setNewValue),
    {
        label: "Scheduled for",
        key: "scheduled_for",
        dto: (data) => getScheduledForInfo(data, setNewValue),
        shown: false,
        sortable: true,
    },
    executionPriorityColumn,
    // Hidden by default here; the toolbar above the table has the bulk actions.
    { ...executionActionsColumn, shown: false },
];

export const messageColumns = (setNewValue) => [
    commonColumns.name({ setNewValue }),
    commonColumns.id(setNewValue),
    commonColumns.modifiedAt(setNewValue),
    {
        label: "Has response",
        key: "response",
        required: false,
        shown: true,
        sortable: true,
        dto: (data: MessageTypeData) => {
            const isResponded = data.response;
            return isResponded ? (
                <ContextMenuWrapper
                    handleClick={() =>
                        setNewValue({
                            field: "response",
                            value: data.response,
                        })
                    }
                >
                    <Icon icon={IconCollection.check} />
                </ContextMenuWrapper>
            ) : null;
        },
    },
];
